import { FARM_DIAGNOSTICS_GHOST_STATE_MESSAGE } from 'constants/uiConstants';
import React, { useMemo } from 'react';
import { formatNumber } from '../Insights/summary2/formatNumber';
import SectionCard from '../Insights/summary2/SectionCard';
import ChartGhostState from './ChartGhostState';
import {
  computeKeyMetricsSummary,
  computeWueTrendFactors,
} from './computeFarmDiagnosticsMetrics';
import TrendChart from './TrendChart';
import TrendKeyMetricsPanel from './TrendKeyMetricsPanel';
import type { FarmDiagnosticsSeries } from './useFarmDiagnosticsSeries';

interface WueTrendDashboardProps {
  series: FarmDiagnosticsSeries;
  showYearOnYear: boolean;
  showRollingAvg: boolean;
  showMarkers: boolean;
  /** Overrides the empty-chart prompt — the plan preview points to the workspace instead of a Yearly Overview it doesn't have. */
  ghostMessage?: string;
}

const formatWue = (v: number) => `${formatNumber(v)} kg/mm`;

/** Dashboard 1 — WUE Index trend for the selected paddock. */
const WueTrendDashboard: React.FC<WueTrendDashboardProps> = ({
  series,
  showYearOnYear,
  showRollingAvg,
  showMarkers,
  ghostMessage = FARM_DIAGNOSTICS_GHOST_STATE_MESSAGE,
}) => {
  const points = useMemo(
    () => series.years.map((y) => ({ year: y.year, value: y.wueIndex })),
    [series.years]
  );
  const markers = useMemo(
    () => series.years.map((y) => ({ year: y.year, tags: y.practiceTags })),
    [series.years]
  );

  const latest = series.years[series.years.length - 1];
  const previous = series.years[series.years.length - 2];
  const summary = computeKeyMetricsSummary(points, series.wueRollingAvg, series.wueTrendByYear);
  const factors = computeWueTrendFactors(latest, previous);
  const hasData = points.some((p) => p.value != null);

  return (
    <div className="flex flex-col gap-6">
      <TrendKeyMetricsPanel
        summary={summary}
        factors={factors}
        formatValue={formatWue}
        yoyUnit="kg/mm"
      />

      <SectionCard id="wueTrend-chart" title="WUE Index Trend" variant="result">
        <ChartGhostState active={!hasData} message={ghostMessage}>
          <TrendChart
            points={points}
            rollingAvg={series.wueRollingAvg}
            trendByYear={series.wueTrendByYear}
            markers={showMarkers ? markers : []}
            formatValue={formatWue}
            valueLabel="WUE Index (kg/mm)"
            showYearOnYear={showYearOnYear}
            showRollingAvg={showRollingAvg}
          />
        </ChartGhostState>
      </SectionCard>
    </div>
  );
};

export default WueTrendDashboard;
